import type React from 'react';

import { Box, Divider, FieldSet, Legend } from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { WATER_SEWER } from '../../../../constants';
import HeaderToggle from './HeaderToggle';
import FixedOrVariableSelector from './FixedOrVariableSelector';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';
import RadioListBox from '../../../Widgets/RadioListBox';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  [WATER_SEWER.FIELD_NAME.IS_ENABLED]: true,
  [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
  [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: null,
  [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
  [WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER]: '',
};

export default function WaterSewer_v5({ section, setSectionValues }: Props): JSX.Element {
  const option1 = WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY;
  const option2 = WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY;
  const waterSewer_isEnabled = section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED);
  const option2Selected = section.getFieldValue(option2);

  function setAdditionalFixedAmountValue(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  function setAdditionalFixedAmountSelected(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: true,
      [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [WATER_SEWER.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED),
    });
  }

  function setAdditionalVariableCostSelected(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: '',
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: true,
    });
  }

  function renderOption2Details(): React.ReactNode {
    return (
      <FixedOrVariableSelector
        legendText="What is the additional fee for water/sewer?"
        fixedAmountSelected={section.getFieldValue(WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED)}
        fixedAmountValue={section.getFieldValue(WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE)}
        setFixedAmountSelected={setAdditionalFixedAmountSelected}
        setFixedAmountValue={setAdditionalFixedAmountValue}
        variableMonthlyCostSelected={section.getFieldValue(WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED)}
        setVariableMonthlySelected={setAdditionalVariableCostSelected}
      />
    );
  }

  return (
    <>
      <HeaderToggle
        header="Water/sewer"
        subheader="Tenant pays in addition to base rent"
        switchOn={waterSewer_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {waterSewer_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <Box css={{ marginBottom: 'layout.tight' }}>
            <FieldSet required legend={<Legend>Who will be responsible for water/sewer set up?</Legend>}>
              <RadioListBox
                section={section}
                setSectionValues={setSectionValues}
                option1FieldName={option1}
                option2FieldName={option2}
                option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                option1Content="Responsible for account set up and direct payments to previously specified service provider"
                option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
              />
            </FieldSet>
          </Box>
          {option2Selected && renderOption2Details()}
        </>
      )}
    </>
  );
}

WaterSewer_v5.description = 'Removal of service provider';
